import { IconX } from '@tabler/icons-react';
import AddMoreImageIcon from '../../../../assets/icons/add-more-image-icon';
import FileUploadIcon from '../../../../assets/icons/file-upload-icon';
import ButtonComponent from '../../../common/button-component/button-component';
import styles from './upload-component.module.css';
import TextButton from '../../../common/TextButton/TextButton';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';

interface UploadComponentProps {
  selectedFiles: File[];
  onFileSelection: (files: File[]) => void;
  hasVideo: boolean;
  maxImages: number;
  removeFile: (index: number) => void;
}

export default function UploadComponent({
  selectedFiles,
  onFileSelection,
  hasVideo,
  maxImages,
  removeFile,
}: UploadComponentProps) {
  const handleFileSelection = (files: File[]) => {
    const newFiles = files.filter((file) => {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      // Only accept image or video files
      if (!isVideo && !isImage) return false;

      // If it's a video and we already have a video, reject it
      if (isVideo && hasVideo) return false;

      // If it's a video and we don't have one yet, accept it
      if (isVideo && !hasVideo) return true;

      // For images, only accept if we haven't reached maxImages
      if (isImage) {
        const currentImageCount = selectedFiles.filter((f) => f.type.startsWith('image/')).length;
        return currentImageCount < maxImages;
      }

      return false;
    });

    if (newFiles.length > 0) {
      onFileSelection(newFiles);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileSelection(files);
  };

  return (
    <div className={styles.uploadArea}>
      <label htmlFor="fileInput" className={styles.label}>
        <span className={styles.uploadContentText}>Upload Content</span>
        {selectedFiles.length === 0 ? (
          <div className={styles.dropZone} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
            <div className={styles.emptyStateContainer}>
              <FileUploadIcon />
              <p>Drop your {hasVideo ? 'images' : 'files'} here, or</p>
              <TextButton
                text="select from computer"
                className={styles.selectButton}
                onClick={() => document.getElementById('fileInput')?.click()}
              />
            </div>
          </div>
        ) : (
          <div className={styles.fileListContainer}>
            {selectedFiles.map((file, index) => (
              <div key={index} className={styles.fileListItem}>
                <div className={styles.fileInfo}>
                  <div className={styles.thumbnailContainer}>
                    {file.type.startsWith('video/') ? (
                      <video src={(file as any).url || URL.createObjectURL(file)} className={styles.thumbnail} controls>
                        <track kind="captions" />
                      </video>
                    ) : (
                      <img
                        src={(file as any).url || URL.createObjectURL(file)}
                        alt="Preview"
                        className={styles.thumbnail}
                      />
                    )}
                  </div>
                  <span className={styles.fileName}>{file.name}</span>
                </div>
                <CustomTooltip title="Remove" position="bottom">
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <IconX size={16} />
                  </button>
                </CustomTooltip>
              </div>
            ))}
          </div>
        )}
      </label>
      <input
        id="fileInput"
        type="file"
        multiple
        accept="image/*, video/*"
        className={styles.hiddenInput}
        onChange={(e) => handleFileSelection(Array.from(e.target.files || []))}
      />
      {!hasVideo && selectedFiles.length > 0 && (
        <CustomTooltip title="You can upload only 20 images." show={selectedFiles.length >= maxImages}>
          <ButtonComponent
            type="button"
            variant="secondary"
            icon={<AddMoreImageIcon />}
            disabled={selectedFiles.length >= maxImages}
            onClick={() => document.getElementById('fileInput')?.click()}
            className={styles.addMoreButton}
          >
            Add More
          </ButtonComponent>
        </CustomTooltip>
      )}
    </div>
  );
}
